/* ============================================================
   ILS — PDP delivery-time patcher.
     TASK-3300/3311  origin: swap the static day range for the REAL one.
     TASK-5776       the sticky-ATC ticker now states the DESTINATION too.

   TWO SLOT KINDS, ONE FILE. Every [data-ils-delivery-days-live] element is
   patched, but what happens depends on where it sits:

   1. THE STICKY-ATC TICKER CHIP (inside .ils-ppf-chip, the marquee under the
      sticky add-to-cart bar on the product page) gets a WHOLE SENTENCE:

          ru   Доставка в Италию за 3–4 дня
          en   Delivery to Italy in 3–4 days
          de   Lieferung nach Italien in 3–4 Tagen
          lv   Piegāde uz Itāliju 3–4 dienās
          lt   Pristatymas į Italiją per 3–4 dienas
          et   Tarne Itaaliasse 3–4 päevaga

      WHY. The server-rendered chip said `custom.product.delivery_days` —
      "Доставка 3–7 дней" — and this file used to swap only the NUMBERS in it,
      leaving "Доставка 3–4 дней": a bare verbal noun followed by a genitive
      plural with no preposition, which is not a Russian sentence, and which
      never named the destination even though the teaser two rows above it did
      ("Доставка в Италию"). The owner rejected exactly that on 2026-10-24.
      It was also a FALSE number for four countries: DE is 3–5 and EE/LV/LT are
      1–2, while the chip asserted the EU-wide 3–7 -> 3–4 swap.

   2. ANY OTHER SLOT keeps the TASK-3300/3311 behaviour byte-for-byte: replace
      the first numeric range in place, touch nothing else. That is the
      historical one-line teaser `.ils-pp__delivery`
      (`custom.product.eu_delivery_html`, "Over 95% of all orders are delivered
      across European Union in 3–7 days"), which renders only when the
      `pdp_teaser` flag is OFF. Its sentence is about the EU as a whole, not
      about one destination, so composing a per-country sentence into it would
      be wrong — and leaving it alone means flipping the flag back cannot
      regress it.

   ONE SOURCE OF TRUTH, DELIBERATELY NOT A SECOND TABLE. Country declensions,
   the day window, the plural rules and the API client all come from
   assets/custom-delivery-core.js — the same objects the 3-line PDP teaser
   (snippets/custom-pdp-delivery.liquid + assets/custom-pdp-delivery.js) paints
   from, so the ticker and the teaser cannot disagree:

     CORE.countryPhrase(cc, loc)  the 27x6 DIRECTIONAL/accusative phrase
                                  ("в Италию", "во Францию", "на Кипр",
                                  "in die Niederlande", "Saksamaale") — never a
                                  preposition concatenated onto a nominative.
     CORE.t(loc, 'title', …)      "Доставка {country}" / "Delivery {country}" /
                                  "Lieferung {country}" … the teaser's own row-1
                                  frame, so both read identically.
     CORE.fetchCountry(cc)        ONE cached promise per country for the whole
                                  page: the ticker reuses the teaser's answer
                                  instead of issuing a second request, so the
                                  two can never show different numbers.
     CORE.dayWindow(d)            the TASK-3183 display rule (a point estimate
                                  widens by one day — CY/MT are 3/3 and show
                                  3–4). The old code did NOT apply it, so the
                                  chip said "3" where the teaser said "3–4".
     ILS_PDPD.daysPhrase(d, loc)  the day noun in the case this sentence frame
                                  governs (de dative Tagen, lv locative dienās,
                                  lt accusative dienas, et comitative päevaga;
                                  en/ru stay plural-aware: 3–4 дня / 3–5 дней).

   COUNTRY AUTHORITY. The destination is the SERVER's market country
   (localization.country.iso_code, seeded as window.ILS_PDP.country and as the
   core config's shop_country) — never a remembered localStorage choice, which
   is the defect TASK-5768 measured on Production and TASK-5772 fixed in the
   teaser: a visitor whose store held "ES" was served /en-pl/ and read
   "Delivery to Spain". A ?dc= QA override still wins, and once the teaser has
   booted its own resolved country is adopted, so on the hidden QA page (preview
   mode) the chip follows the previewed country like the rest of the block.
   A commit-mode page ignores modal-sourced changes exactly as the teaser does:
   the country comes from the next document after POST /localization, so the
   chip never paints a destination the server has not confirmed.

   NEVER A FABRICATED NUMBER. No live window -> the chip states the destination
   alone ("Доставка в Италию"), which is true and grammatical. No destination at
   all (country outside the 27) -> the chip AND its bullet separator are hidden;
   the ticker keeps scrolling with its other items. A number is printed only
   when the Delivery admin returned one for that country.

   DESIGN IS UNTOUCHED: no flag, no emoji, the chip's own truck <svg> is left
   in place (it is a sibling of this span), and the marquee's typography,
   21s animation, bullet separators and sibling chips are not styled from here.
   The track is width:max-content / white-space:nowrap / flex:0 0 auto, so a
   longer sentence widens the loop and cannot clip or wrap.
   ============================================================ */
(function () {
  'use strict';

  var SLOT = '[data-ils-delivery-days-live]';
  var CHIP = 'ils-ppf-chip';
  var SEP = 'ils-ppf-sep';
  var SUPPORTED = { en: 1, ru: 1, de: 1, lv: 1, lt: 1, et: 1 };
  var RANGE_RE = /(\d+)\s*[-‒–—]\s*(\d+)/; /* n - n  /  n – n  /  n — n */

  /* The ticker sentence per locale. `{title}` is the core's own
     "Delivery <declined country>" and `{days}` the window plus its day noun, so
     only the CONNECTOR between them lives here — one short string per language,
     in the case each one governs:
       en  "in"                 de  "in" + dative
       ru  "за" + accusative    lt  "per" + accusative
       lv  locative, no preposition ("3–4 dienās")
       et  comitative, no preposition ("3–4 päevaga")  */
  var TICK = {
    en: '{title} in {days}',
    ru: '{title} за {days}',
    de: '{title} in {days}',
    lv: '{title} {days}',
    lt: '{title} per {days}',
    et: '{title} {days}'
  };
  /* Two German destinations take a prepositional phrase rather than "nach"
     ("in die Niederlande", "in die Slowakei"), and "Lieferung in die
     Niederlande in 3–4 Tagen" stutters. "innerhalb von" + dative is the same
     meaning without the repeated preposition. */
  var DE_WITHIN = '{title} innerhalb von {days}';

  /* Mirror of DAY_IN_SENTENCE in assets/custom-pdp-delivery.js, used ONLY when
     the teaser is not on the page (the `pdp_teaser` flag off). When it is, that
     module's exported daysPhrase() is called instead, so there is exactly one
     live source. Consolidating these four forms into the shared core is a
     follow-up — it cannot be done from this file. */
  var DAY_IN_SENTENCE = { de: 'Tagen', lv: 'dienās', lt: 'dienas', et: 'päevaga' };

  function core() { return window.ILSDeliveryCore || null; }
  function teaser() { var m = window.ILS_PDPD; return (m && typeof m.daysPhrase === 'function') ? m : null; }
  function slots() { return document.querySelectorAll(SLOT); }
  function chipOf(el) { return (el && el.closest) ? el.closest('.' + CHIP) : null; }

  /* A commit placement (the real storefront PDP) only ever shows a country the
     server has confirmed — same rule as the teaser's subscribe guard. */
  function commitMode() { return !!document.querySelector('[data-ils-pdpd][data-ils-pdpd-commit]'); }

  function daysPhrase(d, loc) {
    var CORE = core();
    var m = teaser();
    if (m) return m.daysPhrase(d, loc);
    var w = CORE.dayWindow(d);
    if (!w) return null;
    var range = w.from + '–' + w.to; /* en dash, never em dash */
    return DAY_IN_SENTENCE[loc] ? range + ' ' + DAY_IN_SENTENCE[loc] : CORE.daysText(d, loc);
  }

  function sentence(cc, loc, d) {
    var CORE = core();
    var phrase = String(CORE.countryPhrase(cc, loc));
    var title = CORE.t(loc, 'title', { country: phrase });
    var days = d ? daysPhrase(d, loc) : null;
    if (!days) return title; /* destination only — true, and no invented number */
    var frame = (loc === 'de' && /^in\s/i.test(phrase)) ? DE_WITHIN : (TICK[loc] || TICK.en);
    return frame.split('{title}').join(title).split('{days}').join(days);
  }

  function show(chip, on) {
    if (!chip) return;
    chip.style.display = on ? '' : 'none';
    var sep = chip.nextElementSibling;
    if (sep && sep.classList && sep.classList.contains(SEP)) sep.style.display = on ? '' : 'none';
  }

  /* cc may be null (no destination signal at all). d may be null (no window
     yet, or none on record). Both are painted honestly, never guessed. */
  function paint(cc, loc, d) {
    var text = cc ? sentence(cc, loc, d) : null;
    var list = slots();
    for (var i = 0; i < list.length; i++) {
      var el = list[i], chip = chipOf(el);
      if (chip) {
        if (text == null) { show(chip, false); continue; }
        show(chip, true);
        el.textContent = text; /* textContent: the ticker chip carries no markup */
      } else if (d) {
        /* TASK-3300/3311, unchanged: swap the range inside whatever sentence the
           server rendered, keeping its <b>…</b> markup intact. */
        var CORE = core(), w = CORE.dayWindow(d);
        if (!w) continue;
        var repl = (w.from === w.to) ? String(w.from) : (w.from + '–' + w.to);
        if (RANGE_RE.test(el.innerHTML)) el.innerHTML = el.innerHTML.replace(RANGE_RE, repl);
      }
    }
  }

  function localeOf(CORE) {
    var loc = String(CORE.initialLocale() || '').toLowerCase().split('-')[0];
    return SUPPORTED[loc] ? loc : 'en';
  }

  /* ?dc= QA override > the live teaser's own resolved country > the SERVER's
     market country > a stored manual choice. A stored choice is LAST on
     purpose: it must never outrank the market the shopper is actually browsing
     (TASK-5768). null = no destination -> the chip hides rather than guess. */
  function resolveCountry(CORE) {
    var q = null;
    try { q = new URLSearchParams(location.search).get('dc'); } catch (e) {}
    if (q && CORE.EU27.indexOf(q.toUpperCase()) !== -1) return q.toUpperCase();
    var m = window.ILS_PDPD, st = (m && typeof m.getState === 'function') ? m.getState() : null;
    if (st && st.country && CORE.EU27.indexOf(String(st.country).toUpperCase()) !== -1) return String(st.country).toUpperCase();
    var srv = String((window.ILS_PDP && window.ILS_PDP.country) || (CORE.cfg && CORE.cfg.shop_country) || '').trim().toUpperCase();
    if (CORE.EU27.indexOf(srv) !== -1) return srv;
    var s = (CORE.getStored() || {}).country;
    if (s && CORE.EU27.indexOf(String(s).toUpperCase()) !== -1) return String(s).toUpperCase();
    return null;
  }

  var current = { cc: null, loc: null };

  function load(cc, loc) {
    var CORE = core();
    current.cc = cc; current.loc = loc;
    paint(cc, loc, null); /* destination at once — no "Доставка 3–7 дней" flash */
    if (!cc) return;
    CORE.fetchCountry(cc).then(function (d) {
      if (current.cc !== cc || current.loc !== loc) return; /* a newer pick won */
      var usable = (d && d.ok && !d.empty && CORE.dayWindow(d)) ? d : null;
      paint(cc, loc, usable);
    }).catch(function () { /* paint(…, null) already stands */ });
  }

  function start() {
    var CORE = core();
    if (!slots().length) return;
    load(resolveCountry(CORE), localeOf(CORE));

    /* The teaser boots on the same DOMContentLoaded and registers AFTER this
       module, so its resolved country is readable only on the next task. On the
       storefront it equals the server's market country and nothing changes; on
       the QA preview page it is the previewed country, and adopting it is what
       keeps the chip in step with the block above it. */
    setTimeout(function () {
      var cc = resolveCountry(CORE), loc = localeOf(CORE);
      if (cc !== current.cc || loc !== current.loc) load(cc, loc);
    }, 0);

    CORE.subscribe(function (st) {
      if (!st) return;
      if (st.country && !(commitMode() && st.source === 'modal')) {
        var cc = String(st.country).toUpperCase();
        if (CORE.EU27.indexOf(cc) !== -1 && cc !== current.cc) load(cc, current.loc || localeOf(CORE));
      }
      if (st.locale) {
        var loc = String(st.locale).toLowerCase().split('-')[0];
        loc = SUPPORTED[loc] ? loc : 'en';
        if (loc !== current.loc) load(current.cc, loc);
      }
    });
  }

  /* The core is loaded by snippets/custom-pdp-delivery.liquid, which this
     module's own <script> precedes in document order — both are `defer`, so the
     core has executed by DOMContentLoaded. If the PDP teaser flag is OFF the
     snippet does not render and nothing else pulls the core in, so it is
     fetched from the same /assets/ path this file was served from (the version
     query is dropped; Shopify serves the current bytes). The core early-returns
     if something else defined it first, so a double load is harmless. */
  var SELF = (document.currentScript && document.currentScript.src) || '';

  function withCore(fn) {
    if (core()) { fn(); return; }
    if (!slots().length) return;
    var self = SELF.split('?')[0];
    var url = self.replace(/custom-pdp-delivery-days-live\.js$/, 'custom-delivery-core.js');
    if (url && url !== self && !document.querySelector('script[src*="custom-delivery-core.js"]')) {
      var s = document.createElement('script');
      s.src = url;
      s.onload = function () { if (core()) fn(); };
      document.head.appendChild(s);
      return;
    }
    /* A core tag exists but has not executed yet (or is still downloading). */
    var tries = 0;
    var t = setInterval(function () {
      if (core()) { clearInterval(t); fn(); return; }
      if (++tries > 100) clearInterval(t); /* ~5 s, then the chip keeps whatever the server rendered */
    }, 50);
  }

  function boot() { withCore(start); }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
